// src/components/eventos/components/diretor/ControleEvento.js
import React, { useState, useEffect, useRef } from 'react';
import {
    useToast, useDisclosure,
    AlertDialog, AlertDialogBody, AlertDialogFooter,
    AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import EnviarMensagem from './EnviarMensagem';

// ─── helpers ────────────────────────────────────────────────────────────────
const formatarTempo = (segundos) => {
    const mins = Math.floor(Math.abs(segundos) / 60);
    const secs = Math.abs(segundos) % 60;
    const sinal = segundos < 0 ? '-' : '';
    return `${sinal}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const formatarData = (dataString) => {
    if (!dataString) return 'Data não informada';
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-');
        return `${dia}/${mes}/${ano}`;
    } catch { return 'Data inválida'; }
};

const adicionarMinutos = (hora, minutos) => {
    const [h, m] = hora.split(':').map(Number);
    const total = h * 60 + m + minutos;
    return `${Math.floor(total / 60) % 24}`.padStart(2, '0') + ':' + `${total % 60}`.padStart(2, '0');
};

// ─── inline SVGs ────────────────────────────────────────────────────────────
const CheckIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const StopIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    </svg>
);
const SendIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

// ─── custom toggle ───────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
    <button
        onClick={() => onChange(!checked)}
        style={{
            position: 'relative', width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
            background: checked ? '#7c3aed' : '#e2e8f0', transition: 'background 0.2s', flexShrink: 0,
        }}
    >
        <span style={{
            position: 'absolute', top: '3px', left: checked ? '21px' : '3px', width: '16px', height: '16px',
            borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }} />
    </button>
);

// ─── button styles ───────────────────────────────────────────────────────────
const btnBase = { display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', -apple-system, sans-serif", transition: 'all 0.15s', border: 'none' };

// ────────────────────────────────────────────────────────────────────────────
const ControleEvento = ({ evento, onAtualizarEvento, supabase, clienteId }) => {
    const [tempoAtual, setTempoAtual] = useState(new Date());
    const [processando, setProcessando] = useState(false);
    const [modoAutomatico, setModoAutomatico] = useState(evento?.modo_automatico || false);
    const activeTaskRef = useRef(null);
    const cancelRef = useRef();
    const autoIntervalRef = useRef(null);
    const processandoAutoRef = useRef(false);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isEncerrarOpen, onOpen: onEncerrarOpen, onClose: onEncerrarClose } = useDisclosure();

    // Timer tick
    useEffect(() => {
        const timer = setInterval(() => setTempoAtual(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Scroll para tarefa ativa a cada refresh
    useEffect(() => {
        if (activeTaskRef.current) {
            activeTaskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [evento]);

    // Modo automático
    useEffect(() => {
        const horaAtual = () => {
            const agora = new Date();
            return `${agora.getHours().toString().padStart(2, '0')}:${agora.getMinutes().toString().padStart(2, '0')}`;
        };

        const checarTransicao = async () => {
            if (!evento || processandoAutoRef.current) return;
            const tarefas = evento.tarefas || [];
            const hora = horaAtual();
            const emAndamento = tarefas.find(t => t.status === 'em_andamento');

            if (emAndamento) {
                if (hora >= emAndamento.horaFim) {
                    processandoAutoRef.current = true;
                    await finalizarTarefaAuto(emAndamento);
                    processandoAutoRef.current = false;
                }
            } else {
                const proxima = tarefas.find(t => t.status === 'pendente' && hora >= t.horaInicio);
                if (proxima) {
                    processandoAutoRef.current = true;
                    await iniciarTarefaAuto(proxima);
                    processandoAutoRef.current = false;
                }
            }
        };

        if (modoAutomatico) {
            checarTransicao();
            autoIntervalRef.current = setInterval(checarTransicao, 30000);
        } else {
            clearInterval(autoIntervalRef.current);
        }
        return () => clearInterval(autoIntervalRef.current);
    }, [modoAutomatico, evento]); // eslint-disable-line react-hooks/exhaustive-deps

    const calcularTempoTarefa = (tarefa) => {
        const horaStr = tarefa.horaInicioReal || tarefa.horaInicio;
        if (!horaStr || !horaStr.includes(':')) {
            const duracao = (tarefa.duracao || 0) * 60;
            return { decorrido: 0, restante: duracao, duracao };
        }
        const agora = tempoAtual;
        const [h, m] = horaStr.split(':').map(Number);
        const inicio = new Date(agora);
        inicio.setHours(h, m, 0);
        const decorrido = Math.floor((agora - inicio) / 1000);
        const duracao = (tarefa.duracao || 0) * 60;
        return { decorrido, restante: duracao - decorrido, duracao };
    };

    const iniciarTarefaAuto = async (tarefa) => {
        try {
            const novasTarefas = evento.tarefas.map(t =>
                t.id === tarefa.id ? { ...t, status: 'em_andamento', horaInicioReal: tarefa.horaInicio } : t
            );
            await supabase.from('eventos').update({ tarefas: novasTarefas }).eq('id', evento.id);
            if (onAtualizarEvento) onAtualizarEvento();
        } catch (error) { console.error('Erro ao iniciar tarefa automática:', error); }
    };

    const finalizarTarefaAuto = async (tarefa) => {
        try {
            const horaAtual = () => {
                const agora = new Date();
                return `${agora.getHours().toString().padStart(2,'0')}:${agora.getMinutes().toString().padStart(2,'0')}`;
            };
            const tarefaIdx = evento.tarefas.findIndex(t => t.id === tarefa.id);
            const novasTarefas = [...evento.tarefas];
            novasTarefas[tarefaIdx] = { ...novasTarefas[tarefaIdx], status: 'concluida', horaFimReal: tarefa.horaFim };
            // Só inicia próxima tarefa se o horaInicio já chegou
            const proxIdx = novasTarefas.findIndex((t, i) => i > tarefaIdx && t.status === 'pendente');
            if (proxIdx !== -1 && horaAtual() >= novasTarefas[proxIdx].horaInicio) {
                novasTarefas[proxIdx] = { ...novasTarefas[proxIdx], status: 'em_andamento', horaInicioReal: novasTarefas[proxIdx].horaInicio };
            }
            const todasConcluidas = novasTarefas.every(t => t.status === 'concluida');
            await supabase.from('eventos').update({
                tarefas: novasTarefas,
                status: todasConcluidas ? 'concluido' : 'em_andamento',
                hora_fim_real: todasConcluidas ? tarefa.horaFim : null,
            }).eq('id', evento.id);
            if (todasConcluidas) {
                toast({ title: 'Evento concluído', description: 'Todas as tarefas foram concluídas automaticamente.', status: 'success', duration: 4000, isClosable: true });
            }
            if (onAtualizarEvento) onAtualizarEvento();
        } catch (error) { console.error('Erro ao finalizar tarefa automática:', error); }
    };

    const finalizarTarefa = async (tarefaId) => {
        if (!evento) return;
        setProcessando(true);
        try {
            const agora = new Date();
            const horaAtual = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const tarefaIdx = evento.tarefas.findIndex(t => t.id === tarefaId);
            const novasTarefas = [...evento.tarefas];
            novasTarefas[tarefaIdx] = { ...novasTarefas[tarefaIdx], status: 'concluida', horaFimReal: horaAtual };

            for (let i = tarefaIdx + 1; i < novasTarefas.length; i++) {
                if (novasTarefas[i].status === 'pendente') {
                    const novaHoraInicio = i === tarefaIdx + 1 ? horaAtual : novasTarefas[i - 1].horaFim;
                    const novaHoraFim = adicionarMinutos(novaHoraInicio, novasTarefas[i].duracao);
                    novasTarefas[i] = { ...novasTarefas[i], horaInicio: novaHoraInicio, horaFim: novaHoraFim, status: i === tarefaIdx + 1 ? 'em_andamento' : 'pendente', horaInicioReal: i === tarefaIdx + 1 ? horaAtual : null };
                }
            }

            const todasConcluidas = novasTarefas.every(t => t.status === 'concluida');
            const { error } = await supabase.from('eventos').update({
                tarefas: novasTarefas, status: todasConcluidas ? 'concluido' : 'em_andamento',
                hora_fim_real: todasConcluidas ? horaAtual : null,
            }).eq('id', evento.id);
            if (error) throw error;

            toast({ title: 'Tarefa finalizada', description: todasConcluidas ? 'Evento concluído com sucesso!' : 'Horários ajustados automaticamente', status: 'success', duration: 3000, isClosable: true });
            if (onAtualizarEvento) onAtualizarEvento();
        } catch (error) {
            toast({ title: 'Erro ao finalizar tarefa', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally { setProcessando(false); }
    };

    const encerrarEvento = async () => {
        if (!evento) return;
        setProcessando(true);
        try {
            const agora = new Date();
            const horaAtual = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const { error } = await supabase.from('eventos').update({ status: 'cancelado', hora_fim_real: horaAtual }).eq('id', evento.id);
            if (error) throw error;
            toast({ title: 'Evento encerrado', description: 'O evento foi encerrado antecipadamente.', status: 'warning', duration: 4000, isClosable: true });
            onEncerrarClose();
            if (onAtualizarEvento) onAtualizarEvento();
        } catch (error) {
            toast({ title: 'Erro ao encerrar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally { setProcessando(false); }
    };

    if (!evento) return null;

    const tarefasConcluidas = evento.tarefas?.filter(t => t.status === 'concluida').length || 0;
    const totalTarefas = evento.tarefas?.length || 0;
    const progressoGeral = totalTarefas > 0 ? (tarefasConcluidas / totalTarefas) * 100 : 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: "'Inter', -apple-system, sans-serif" }}>

            {/* ── Cabeçalho sticky ─────────────────────────────────────────── */}
            <div style={{
                background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
                borderRadius: '16px', padding: '20px', position: 'sticky', top: '80px', zIndex: 9,
            }}>
                {/* Top row: badges + controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '100px', background: 'rgba(44,203,144,0.1)', border: '1px solid rgba(44,203,144,0.25)', color: '#2CCB90', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em' }}>
                            EM ANDAMENTO
                        </span>
                        <span style={{ padding: '4px 10px', borderRadius: '100px', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', color: '#2563eb', fontSize: '11px', fontWeight: 600 }}>
                            📅 {formatarData(evento.data)}
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {/* Toggle automático */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Toggle checked={modoAutomatico} onChange={(val) => {
                                setModoAutomatico(val);
                                toast({
                                    title: val ? 'Modo automático ativado' : 'Modo automático desativado',
                                    description: val ? 'As tarefas avançarão conforme o horário programado' : 'Controle manual reativado',
                                    status: val ? 'info' : 'warning', duration: 3000, isClosable: true,
                                });
                            }} />
                            <span style={{ fontSize: '12px', fontWeight: 600, color: modoAutomatico ? '#7c3aed' : '#94a3b8' }}>
                                {modoAutomatico ? 'Automático' : 'Manual'}
                            </span>
                        </div>

                        <button onClick={onEncerrarOpen} disabled={processando}
                            style={{ ...btnBase, padding: '8px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444' }}>
                            <StopIcon /> Encerrar Evento
                        </button>
                    </div>
                </div>

                {/* Título */}
                <h2 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{evento.titulo}</h2>
                {evento.local && <p style={{ margin: '0 0 2px', fontSize: '13px', color: '#64748b' }}>📍 {evento.local}</p>}
                {evento.descricao && <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b' }}>{evento.descricao}</p>}

                {/* Stats row */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    {evento.hora_inicio_real && (
                        <div>
                            <p style={{ margin: '0 0 2px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Início</p>
                            <p style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2563eb' }}>{evento.hora_inicio_real}</p>
                        </div>
                    )}
                    <div>
                        <p style={{ margin: '0 0 2px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Progresso</p>
                        <p style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#7c3aed' }}>{tarefasConcluidas}/{totalTarefas}</p>
                    </div>
                    <div>
                        <p style={{ margin: '0 0 2px', fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Percentual</p>
                        <p style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2CCB90' }}>{Math.round(progressoGeral)}%</p>
                    </div>
                </div>

                {/* Barra de progresso geral */}
                <div style={{ height: '8px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progressoGeral}%`, borderRadius: '100px', background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', transition: 'width 0.5s ease' }} />
                </div>
            </div>

            {/* ── Lista de Tarefas ──────────────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {evento.tarefas?.map((tarefa, idx) => {
                    const { decorrido, restante, duracao } = calcularTempoTarefa(tarefa);
                    const progresso = Math.min(100, Math.max(0, (decorrido / duracao) * 100));
                    const atrasado = restante < 0;
                    const concluida = tarefa.status === 'concluida';
                    const emAndamento = tarefa.status === 'em_andamento';

                    let cardBg = '#ffffff';
                    let cardBorder = '#e2e8f0';
                    if (emAndamento) { cardBg = 'rgba(139,92,246,0.04)'; cardBorder = 'rgba(139,92,246,0.25)'; }
                    if (concluida) { cardBg = 'rgba(44,203,144,0.03)'; cardBorder = 'rgba(44,203,144,0.15)'; }

                    return (
                        <div key={tarefa.id} ref={emAndamento ? activeTaskRef : null}
                            style={{ background: cardBg, border: `${emAndamento ? '2px' : '1px'} solid ${cardBorder}`, borderRadius: '14px', padding: '18px', opacity: concluida ? 0.72 : 1, transition: 'all 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>

                            {/* Cabeçalho da tarefa */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <span style={{ padding: '3px 10px', borderRadius: '7px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '12px', fontWeight: 700 }}>
                                        #{idx + 1}
                                    </span>
                                    <span style={{ fontSize: '15px', fontWeight: 700, color: concluida ? '#94a3b8' : '#0f172a' }}>{tarefa.titulo}</span>
                                    {emAndamento && (
                                        <span style={{ padding: '3px 9px', borderRadius: '100px', background: 'rgba(44,203,144,0.1)', border: '1px solid rgba(44,203,144,0.2)', color: '#2CCB90', fontSize: '11px', fontWeight: 700 }}>
                                            EM ANDAMENTO
                                        </span>
                                    )}
                                    {emAndamento && modoAutomatico && (
                                        <span style={{ padding: '3px 9px', borderRadius: '100px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '11px', fontWeight: 700 }}>
                                            AUTO
                                        </span>
                                    )}
                                    {concluida && (
                                        <span style={{ padding: '3px 9px', borderRadius: '100px', background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.15)', color: '#64748b', fontSize: '11px', fontWeight: 700 }}>
                                            ✓ CONCLUÍDA
                                        </span>
                                    )}
                                    {atrasado && !concluida && (
                                        <span style={{ padding: '3px 9px', borderRadius: '100px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: '11px', fontWeight: 700 }}>
                                            ⚠ ATRASADO
                                        </span>
                                    )}
                                </div>
                                <span style={{ padding: '3px 10px', borderRadius: '7px', background: 'rgba(45,184,225,0.08)', border: '1px solid rgba(45,184,225,0.15)', color: '#2DB8E1', fontSize: '12px', fontWeight: 600 }}>
                                    ⏱ {tarefa.duracao} min
                                </span>
                            </div>

                            {/* Horários programados */}
                            <div style={{ padding: '7px 12px', background: '#eff6ff', borderLeft: '3px solid #93c5fd', borderRadius: '6px', marginBottom: '10px', fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>
                                ⏰ {tarefa.horaInicio} – {tarefa.horaFim}
                            </div>

                            {/* Horários reais */}
                            {(tarefa.horaInicioReal || tarefa.horaFimReal) && (
                                <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                    {tarefa.horaInicioReal && (
                                        <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>✓ Início real: {tarefa.horaInicioReal}</span>
                                    )}
                                    {tarefa.horaFimReal && (
                                        <span style={{ fontSize: '12px', color: '#2CCB90', fontWeight: 600 }}>✓ Fim real: {tarefa.horaFimReal}</span>
                                    )}
                                </div>
                            )}

                            {/* Responsável / local */}
                            {(tarefa.responsavel || tarefa.local) && (
                                <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                    {tarefa.responsavel && <span style={{ fontSize: '12px', color: '#64748b' }}><strong style={{ color: '#1e293b' }}>Responsável:</strong> {tarefa.responsavel}</span>}
                                    {tarefa.local && <span style={{ fontSize: '12px', color: '#64748b' }}><strong style={{ color: '#1e293b' }}>Local:</strong> {tarefa.local}</span>}
                                </div>
                            )}

                            {/* Descrição texto */}
                            {tarefa.descricao_tipo === 'texto' && tarefa.descricao && (
                                <div style={{ padding: '8px 10px', background: 'rgba(139,92,246,0.05)', borderLeft: '3px solid #a78bfa', borderRadius: '6px', marginBottom: '10px' }}>
                                    <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Descrição</p>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{tarefa.descricao}</p>
                                </div>
                            )}

                            {/* Descrição lista */}
                            {tarefa.descricao_tipo === 'lista' && tarefa.descricao_items?.length > 0 && (
                                <div style={{ padding: '8px 10px', background: 'rgba(44,203,144,0.05)', borderLeft: '3px solid #2CCB90', borderRadius: '6px', marginBottom: '10px' }}>
                                    <p style={{ margin: '0 0 6px', fontSize: '10px', fontWeight: 700, color: '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Itens da Tarefa</p>
                                    {tarefa.descricao_items.map((item, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#64748b', padding: '2px 0' }}>
                                            <span style={{ color: '#2CCB90', marginTop: '1px', flexShrink: 0 }}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            </span>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Controles — só para tarefa em andamento */}
                            {emAndamento && (
                                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
                                        {/* Timer */}
                                        <div>
                                            <p style={{ margin: 0, fontSize: '36px', fontWeight: 800, color: atrasado ? '#ef4444' : '#2CCB90', lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                                                {formatarTempo(restante)}
                                            </p>
                                            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                {atrasado ? 'tempo em atraso' : 'tempo restante'}
                                            </p>
                                        </div>

                                        {/* Botões */}
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {clienteId && (
                                                <button onClick={onOpen}
                                                    style={{ ...btnBase, padding: '9px 16px', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', color: '#2563eb' }}>
                                                    <SendIcon /> Mensagem
                                                </button>
                                            )}
                                            <button onClick={() => finalizarTarefa(tarefa.id)} disabled={processando}
                                                style={{ ...btnBase, padding: '9px 20px', minWidth: '160px', justifyContent: 'center', background: processando ? 'rgba(44,203,144,0.06)' : 'linear-gradient(135deg, #2CCB90, #22a876)', color: processando ? 'rgba(44,203,144,0.4)' : '#fff', boxShadow: processando ? 'none' : '0 4px 16px rgba(44,203,144,0.25)' }}>
                                                {processando ? (
                                                    <>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'ce-spin 1s linear infinite' }}>
                                                            <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
                                                            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                                                            <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
                                                        </svg>
                                                        Finalizando...
                                                    </>
                                                ) : (
                                                    <><CheckIcon /> Finalizar Tarefa</>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Barra de progresso da tarefa */}
                                    <div>
                                        <div style={{ height: '8px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden', marginBottom: '6px' }}>
                                            <div style={{ height: '100%', width: `${progresso}%`, borderRadius: '100px', background: atrasado ? 'linear-gradient(90deg, #ef4444, #f87171)' : 'linear-gradient(90deg, #7c3aed, #a78bfa)', transition: 'width 1s linear' }} />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8' }}>
                                            <span style={{ fontWeight: 600 }}>{Math.round(progresso)}% concluído</span>
                                            <span>{Math.floor(decorrido / 60)}:{(decorrido % 60).toString().padStart(2, '0')} decorridos</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <style>{`@keyframes ce-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            {/* Modal EnviarMensagem */}
            {clienteId && (
                <EnviarMensagem isOpen={isOpen} onClose={onClose} evento={evento} clienteId={clienteId} supabase={supabase} />
            )}

            {/* AlertDialog Encerrar */}
            <AlertDialog isOpen={isEncerrarOpen} leastDestructiveRef={cancelRef} onClose={onEncerrarClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent bg="#ffffff" border="1px solid #e2e8f0" borderRadius="16px" color="#1e293b" fontFamily="'Inter', -apple-system, sans-serif" boxShadow="0 20px 60px rgba(0,0,0,0.15)">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" borderBottom="1px solid #e2e8f0" pb={3} color="#0f172a">
                            Encerrar Evento
                        </AlertDialogHeader>
                        <AlertDialogBody py={5} color="#64748b" fontSize="14px">
                            Tem certeza que deseja encerrar o evento <strong style={{ color: '#0f172a' }}>{evento.titulo}</strong> antecipadamente? Esta ação não pode ser desfeita.
                        </AlertDialogBody>
                        <AlertDialogFooter borderTop="1px solid #e2e8f0" gap={3}>
                            <button ref={cancelRef} onClick={onEncerrarClose} disabled={processando}
                                style={{ ...btnBase, padding: '9px 18px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>
                                Cancelar
                            </button>
                            <button onClick={encerrarEvento} disabled={processando}
                                style={{ ...btnBase, padding: '9px 18px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', boxShadow: '0 4px 14px rgba(239,68,68,0.2)' }}>
                                {processando ? 'Encerrando...' : 'Encerrar'}
                            </button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </div>
    );
};

export default ControleEvento;
